import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDeepLinkRedirect } from '../hooks/useDeepLink';
import { supabase } from '../lib/supabase';

/**
 * Handles direct article links (/a/{id}).
 * These are links to articles in a user's own library and require authentication.
 */
export function DirectArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { saveIntendedDestination } = useDeepLinkRedirect();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      // Save where we wanted to go, then redirect to auth
      saveIntendedDestination();
      navigate('/app', { replace: true });
      return;
    }

    fetchArticle();
  }, [user, authLoading, id]);

  async function fetchArticle() {
    if (!id) {
      setError('No article ID provided');
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('articles')
        .select('id, title, content, url, saved_at')
        .eq('id', id)
        .eq('user_id', user!.id)
        .single();

      if (fetchError || !data) {
        throw new Error('Article not found');
      }

      // Redirect to the app with the article open
      navigate(`/app#article/${data.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load article');
      setLoading(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="direct-article loading">
        <div className="spinner" />
        <p>Loading article...</p>
        <style>{styles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="direct-article error">
        <div className="error-icon">📄</div>
        <h1>Article Not Found</h1>
        <p>{error}</p>
        <button className="btn-primary" onClick={() => navigate('/app')}>
          Go to Library
        </button>
        <style>{styles}</style>
      </div>
    );
  }

  return null;
}

const styles = `
  .direct-article {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    background: var(--bg-primary, #0a0a0a);
    color: var(--text-primary, #fafaf9);
    text-align: center;
  }

  .error-icon {
    font-size: 4rem;
    opacity: 0.5;
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
  }

  p {
    color: var(--text-secondary, #a1a1aa);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(249, 115, 22, 0.2);
    border-top-color: #f97316;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .btn-primary {
    background: #f97316;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    text-decoration: none;
    margin-top: 1rem;
  }

  .btn-primary:hover {
    background: #ea580c;
  }
`;
